import React, { useContext, useEffect, useState, useCallback } from 'react';
import Box from '@amzn/meridian/box';
import moment from 'moment';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import { FormattedMessage, useIntl } from 'react-intl';
import { renderUrl, urls } from '../../../config/Url';
import { EmptyProfile, ExtendedUser, Profile } from '../../../Types';
import { StateContext } from '../../../GlobalStateStore';
import { getCurrentWeek, getCurrentWeekBoundriesList } from '../../../Util';
import DataState from '../../../Types/DataState';
import DataSource from '../../../api';
import ReviewBar from '../../../commons/formLibrary/ReviewBar';
import { styles } from './OffRoadDriverReviewForm.styles';
import {
  grabExtraFieldByID,
  getDefaultMessageByID,
  mergeFeedbackIntoContactDetails,
  getCheckedValueSelectionAsString,
  scrollToStepperColumns,
  isFormReviewedOrDelivered,
} from '../../../Util/Form/helpers';
import {
  QuestionOption,
  DropdownItem,
  Section,
  Question,
  RadioButtonType,
  AnsweredQuestionItem,
  PageItem,
  StepperBulletPointConfig,
} from '../../../Util/Form/formData.types';
import {
  DynamicFormAnswerInput,
  I18NContentInput,
  FormDetailsInput,
} from '../../../Util/Form/backendFormData.types';
import RenderDropdownsFromData from '../../../Util/Form/RenderDropdownsFromData';
import {
  ConsolidatedContact,
  ContactDetailsInput,
  ContactStatusOptionsObject,
  ContactViewTabObject,
  GoodRecommendationOptionsObject,
  I18NContent,
  RecommendedContactStatusOptionsObject,
} from '../../../api/CoachingNotes/schema';
import ContactReviewFeedbackModal from '../../ContactReview/Modal/FeedbackModal/ContactReviewFeedbackModal';
import {
  FormSaveType,
  StepperProgressTypes,
} from '../../../Util/Form/formData.constants';
import RadioButtonsFromData from '../../../Util/Form/RadioButtonsFromData';
import Card, { CardRadioButton } from '@amzn/meridian/card';
import Textarea from '@amzn/meridian/textarea';
import { getLocaleString } from '../../../translations';
import {
  fetchContactStatus,
  getFormClickstreamTag1AndTag2,
  handleRealtimeHMDContact,
  OUTLIER_INGRESS,
} from '../utils/helper';
import TimeoutToast from '../../CoachingForm/AutoSave/TimeoutToast';
import { ContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/types';
import { EmptyContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/helper';
import { useNavigate } from 'react-router';
import DraftSavedToast from '../../../Util/Form/DraftSavedToast';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';

interface OffRoadDriverReviewFormState {
  dropdownItems: DropdownItem[];
  radioButtonItems: RadioButtonType[];
  stepperProgress: { [key: string]: StepperProgressTypes };
  pages: PageItem[];
  currentPage: number;
  questionsAnswered: { [key: string]: AnsweredQuestionItem };
  nextButtonDisabled: boolean;
  stepperBulletPoints: StepperBulletPointConfig;
  contactInfo: ConsolidatedContact;
  nextButtonRefresh: number;
}

interface OffRoadDriverReviewFormProps {
  userId: string | undefined;
  contactId: string | undefined;
  selectedVertical: string;
  formType: string;
  ccxDetailsInfo: any;
  ingress: string | null;
  passedProfile?: ExtendedUser;
}

type ReviewBarItem = {
  expert: I18NContent[];
  intermediate: I18NContent[];
  novice: I18NContent[];
};

type FormInformation = {
  reviewBarCriteria: { [key: string]: ReviewBarItem };
  stepperBulletPoints: { [key: string]: StepperBulletPointConfig };
  defaultDropdownItems: DropdownItem[];
  defaultRadioButtons: RadioButtonType[];
  additionalQuestionContext: { [key: string]: I18NContent };
};

const csaFields = (
  type: string,
  subtype: string,
  contentID: string,
  ingress: string | null,
  isCCX?: boolean
) => ({
  'data-csa-c-type': type,
  'data-csa-c-slot-id': `'offroad_review-${subtype}-button-slot'`,
  'data-csa-c-content-id': `offroad_review-${subtype}-button-content-id-${contentID}`,
  ...getFormClickstreamTag1AndTag2(ingress, isCCX),
  'data-csa-c-tag3': `Offroad Review`,
});

const MAX_STEPS_PER_PAGE = 3;

// GGGG refers to the year of the week. This may be different from YYYY
// When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
// In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
const startWeek = moment().subtract(4, 'w').format('GGGG-WW');
const endWeek = moment().format('GGGG-WW');
const date = getCurrentWeekBoundriesList();

const OffRoadDriverReviewForm: React.FC<OffRoadDriverReviewFormProps> = ({
  userId,
  contactId,
  selectedVertical,
  formType,
  ccxDetailsInfo,
  passedProfile,
  ingress,
}) => {
  const globalState = useContext(StateContext);
  const navigate = useNavigate();
  const initialProfile = passedProfile
    ? { ...EmptyProfile, user: passedProfile }
    : EmptyProfile;
  const [profile, setProfile] = useState<Profile>(initialProfile);
  const [emailChecked, setEmailChecked] = useState(false);
  const [, setProfileState] = useState(DataState.LOADING);
  const { inspireOptions } = globalState?.state;
  const { cscEntityPrefix, isAdmin, requesterUserID } = inspireOptions.data;
  //TODO pull data from backend
  const apiNaws = DataSource.getDataFeed('NAWS');
  const [driverFormData, setDriverFormData] = useState<any>(undefined);
  const [driverFormDataState, setDriverFormDataState] = useState(
    DataState.LOADING
  );
  const enableSDSOffroadDriverFormUpdates = isFeatureEnabled(
    Feature.ENABLE_SDS_OFFROAD_DRIVER_FORM_UPDATES
  );

  const [showContactReviewFeedbackModal, setShowContactReviewFeedbackModal] =
    useState<{ open: boolean; status?: FormSaveType }>({
      open: false,
    });

  const [contactIDs, setContactIDs] = useState<string[]>([]);
  const [contactList, setContactList] = useState<ConsolidatedContact[]>([]);
  const [contactStatusDetails, setContactStatusDetails] =
    useState<ContactStatus>(EmptyContactStatus);
  const [contactStatusDetailsState, setContactStatusDetailsState] = useState(
    DataState.LOADING
  );
  const [formInformation, setFormInformation] = useState<
    Partial<FormInformation>
  >({});
  const [stepperKeys, setStepperKeys] = useState<string[]>([]);

  const [contactListState, setContactListState] = useState(DataState.LOADING);
  const [nextButtonRefresh, setNextButtonRefresh] = useState(0);

  const [formStatusCode, setFormStatusCode] = useState({});
  const [formStatusCodeState, setFormStatusCodeState] = useState(
    DataState.INITIALIZED
  );
  const [outlierResp, setOutlierResp] = useState({});
  const [outlierRespState, setOutlierRespState] = useState(
    DataState.INITIALIZED
  );
  const [creationTimestamp, setCreationTimestamp] = useState<
    string | undefined
  >(undefined);

  const [formState, setFormState] = useState<
    Partial<OffRoadDriverReviewFormState>
  >({
    dropdownItems: [],
    radioButtonItems: [],
    currentPage: 0,
    nextButtonDisabled: true,
  });

  // Toast
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showDraftSavedToast, setShowDraftSavedToast] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [exceptionMessage, setExceptionMessage] = useState('');

  const [checkedValue, setCheckedValue] = useState(
    formState?.pages?.[formState?.currentPage ?? 0]?.type ?? 'Accuracy'
  );

  const [contactSummary, setContactSummary] = useState('');

  const handleContactSummaryChange = (data: any) => {
    setContactSummary(data);
  };

  const [showStepperColumns, setShowStepperColumns] = useState(false);

  const [associateSite, setAssociateSite] = useState('');

  const handleAssociateSiteChange = (data: any) => {
    setAssociateSite(data);
  };

  const [correctSOP, setCorrectSOP] = useState('');

  const handleCorrectSOP = (data: any) => {
    setCorrectSOP(data);
  };

  const [missingSOP, setMissingSOP] = useState('');

  const handleMissingSOP = (data: any) => {
    setMissingSOP(data);
  };

  const [sopUsedDuringAudit, setSopUsedDuringAudit] = useState('');

  const handleSopUsedDuringAudit = (data: any) => {
    setSopUsedDuringAudit(data);
  };

  const intl = useIntl();

  useEffect(() => {
    if (
      inspireOptions.state === DataState.LOADED &&
      userId &&
      selectedVertical !== ''
    ) {
      apiNaws.getFormTemplateData(
        {
          FORM_ID: 'OFFROAD_DRIVER_REVIEW_FORM',
          VERSION: enableSDSOffroadDriverFormUpdates ? '3' : '1',
        },
        setDriverFormData,
        setDriverFormDataState
      );
    }
  }, [inspireOptions.state, userId, selectedVertical]);

  useEffect(() => {
    if (contactId && contactList.length > 0) {
      const filteredContact = contactList.filter(
        contact => contact.contactID === contactId
      );
      if (filteredContact.length > 0) {
        // Check if this review is first time to be reviewed
        const contact = filteredContact[0];
        if (contact.contactInfo === null) {
          setCreationTimestamp(new Date().toISOString());
        }
      }
    }
  }, [contactId, contactList]);

  const refreshContacts = useCallback(() => {
    const { userName } = profile.user;
    if (userName !== '') {
      const userID = userId ? `${cscEntityPrefix}:${userId}` : '';
      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userID,
        userName,
        startWeek,
        endWeek
      );
      apiNaws.getConsolidatedContacts(
        queryVariablesNaws,
        setContactList,
        setContactListState
      );
    }
  }, [profile, userId]);

  useEffect(() => {
    fetchContactStatus(
      ingress,
      profile,
      cscEntityPrefix,
      date,
      apiNaws,
      setContactStatusDetails,
      setContactStatusDetailsState
    );
  }, [ingress, profile]);

  useEffect(() => {
    handleRealtimeHMDContact(
      contactId,
      ingress,
      contactList,
      contactListState,
      contactStatusDetailsState,
      profile,
      cscEntityPrefix,
      contactStatusDetails,
      setContactList,
      setContactListState
    );
  }, [
    contactId,
    ingress,
    contactList,
    profile,
    contactListState,
    contactStatusDetailsState,
  ]);

  useEffect(() => {
    if (
      formStatusCodeState === DataState.ERROR ||
      outlierRespState === DataState.ERROR
    ) {
      setShowErrorToast(true);
    }

    if (formStatusCodeState === DataState.LOADED && isSavingDraft) {
      setShowDraftSavedToast(true);
    }
    setIsSavingDraft(false);
  }, [formStatusCodeState, outlierRespState]);

  useEffect(() => {
    refreshContacts();
  }, [profile]);

  useEffect(() => {
    handleContactSummaryChange('');
    handleAssociateSiteChange('');
    handleCorrectSOP('');
    handleMissingSOP('');
    handleSopUsedDuringAudit('');
  }, []);

  useEffect(() => {
    const { currentPage, pages, questionsAnswered } = formState;
    if (currentPage !== undefined && pages && questionsAnswered) {
      setFormState({ ...formState, nextButtonRefresh });
      if (nextButtonRefresh === 0) {
        updateStepperProgress();
      }
    }
  }, [formState.questionsAnswered, nextButtonRefresh]);

  const updateStepperProgress = () => {
    const map: { [key: string]: number } = {};
    (formState?.pages ?? []).forEach(
      p => (map[p.type] = (map[p.type] ?? 0) + p.points.length)
    );
    const totalCompleted = Object.keys(
      formState?.questionsAnswered ?? {}
    )?.filter(k => !k.includes('*')).length;
    const stepperProgressMap: { [key: string]: StepperProgressTypes } = {};
    // build the progress now
    let previousMinimum = 0;
    let totalCounted = 0;
    Object.keys(map).forEach(mapKey => {
      const value = map[mapKey];
      totalCounted += value;
      if (totalCompleted > totalCounted) {
        stepperProgressMap[mapKey] = StepperProgressTypes.PAST;
      } else if (
        totalCompleted === totalCounted ||
        totalCompleted > previousMinimum
      ) {
        stepperProgressMap[mapKey] = StepperProgressTypes.PRESENT;
      } else {
        stepperProgressMap[mapKey] = StepperProgressTypes.FUTURE;
      }
      previousMinimum = totalCounted;
    });

    setFormState({
      ...formState,
      stepperProgress: stepperProgressMap,
      nextButtonDisabled: totalCompleted < 0, // TODO update after translations are showing up correctly
    });
  };

  useEffect(() => {
    if (
      driverFormDataState === DataState.LOADED &&
      driverFormData.SECTIONS !== undefined
    ) {
      let defaultRadioButtonData =
        driverFormData.SECTIONS.find(
          (section: Section) =>
            section.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_RADIO_BUTTONS'
        )?.QUESTIONS.map((question: Question) => ({
          type: question.QUESTION_ID.toLowerCase(),
          placeholder: question.QUESTION_TEXT,
          options: question.QUESTION_OPTIONS || [],
          checked: undefined,
        })) || [];

      setFormState({
        ...formState,
        contactInfo:
          contactList?.filter(c => c.contactID === contactId)?.[0] ?? {},
        dropdownItems: handleSpecialDropdownItems(
          formState?.dropdownItems ?? [],
          driverFormData
        ),
        radioButtonItems: defaultRadioButtonData,
      });
    }
  }, [driverFormData, contactId, driverFormDataState, selectedVertical]);

  useEffect(() => {
    // Logic Below is about auto-pupulate value after saving
    if (contactId) {
      const contactInfo = contactList.find(
        contact => contact.contactID === contactId
      );
      if (contactInfo) {
        const formReviews = contactInfo?.formReviews?.filter(
          f => f.formID === 'OFFROAD_DRIVER_REVIEW_FORM'
        );

        const radioQuesitonList = [
          'WAS_CORRECT_SOP',
          'WAS_ACCOUNT_SECURITY_WAS_NOT_FOLLOWED',
          'WAS_ACCOUNT_SECURITY_VERIFIED',
          'ADHERE_TO_POLICY',
          'WAS_IT_ESCALATED_CORRECTLY',
          'WAS_IT_ESCALATED',
          'CONTACT_RELATED_TO_SAFETY_AND_INCIDENT',
        ];

        const freetextList = [
          'ASSOCIATE_SITE',
          'CONTACT_SUMMARY',
          'CORRECT_SOP_NAME',
          'SOP_MISSING',
          'SOP_USED_DURING_AUDIT',
        ];

        const questionsAnswered = formReviews?.[0]?.formAnswer
          .filter(answer => !freetextList.includes(answer.questionID))
          .reduce(
            (
              acc: { [key: string]: AnsweredQuestionItem },
              answer: DynamicFormAnswerInput
            ) => {
              acc[answer.questionText.defaultMessage] = {
                comments: answer.answerText || '',
                checkedValue: answer.value,
              };
              return acc;
            },
            {}
          );

        const dropdownQuestions =
          driverFormData?.SECTIONS?.filter(
            (s: Section) =>
              s.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_DROPDOWNS'
          )?.[0]?.QUESTIONS ?? [];

        const dropdownItems = formReviews?.[0]?.formAnswer
          .filter(
            (answer: any) =>
              (dropdownQuestions as any[]).some(
                ({ QUESTION_ID }) => QUESTION_ID === answer?.questionID
              ) &&
              !radioQuesitonList.includes(answer.questionID) &&
              !freetextList.includes(answer.questionID)
          )
          .map((answer: any) => {
            const type = answer?.questionID?.toLowerCase();
            // we need to find the corresponding question options in data

            const options =
              Object.keys(dropdownQuestions).length === 0
                ? [answer?.selectedOption]
                : dropdownQuestions?.filter(
                    (d: any) =>
                      d.QUESTION_TEXT.TRANSLATION_ID ===
                      answer?.questionText?.translationID
                  )?.[0]?.QUESTION_OPTIONS;

            return {
              type,
              placeholder: {
                TRANSLATION_ID: answer?.questionText.translationID,
                DEFAULT_MESSAGE: answer?.questionText.defaultMessage,
              },
              options,
              selected: answer?.selectedOption?.defaultMessage,
            };
          });

        //@TODO - Refactor this logic
        const summaryNote = formReviews?.[0]?.formAnswer?.filter(
          (f: any) => f.questionID === 'CONTACT_SUMMARY'
        )?.[0];

        if (summaryNote) {
          setContactSummary(summaryNote.answerText ?? '');
        }

        const siteNote = formReviews?.[0]?.formAnswer?.filter(
          (f: any) => f.questionID === 'ASSOCIATE_SITE'
        )?.[0];
        if (siteNote) {
          setAssociateSite(siteNote.answerText ?? '');
        }

        const correctSOP = formReviews?.[0]?.formAnswer?.filter(
          (f: any) => f.questionID === 'CORRECT_SOP_NAME'
        )?.[0];

        if (correctSOP) {
          setCorrectSOP(correctSOP.answerText ?? '');
        }

        const sopMissing = formReviews?.[0]?.formAnswer?.filter(
          (f: any) => f.questionID === 'SOP_MISSING'
        )?.[0];

        if (sopMissing) {
          setMissingSOP(sopMissing.answerText ?? '');
        }

        const sopUsed = formReviews?.[0]?.formAnswer?.filter(
          (f: any) => f.questionID === 'SOP_USED_DURING_AUDIT'
        )?.[0];

        if (sopUsed) {
          setSopUsedDuringAudit(sopUsed.answerText ?? '');
        }

        const radioQuestions =
          driverFormData?.SECTIONS?.filter(
            (s: Section) =>
              s.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_RADIO_BUTTONS'
          )?.[0]?.QUESTIONS ?? {};

        const radioItems = formReviews?.[0]?.formAnswer
          .filter((answer: any) =>
            radioQuesitonList.includes(answer.questionID)
          )
          .map((answer: any) => {
            const type = answer?.questionID?.toLowerCase();
            // we need to find the corresponding question options in data
            const options =
              Object.keys(radioQuestions).length === 0
                ? [answer?.selectedOption]
                : radioQuestions?.filter(
                    (d: any) =>
                      d.QUESTION_TEXT.TRANSLATION_ID ===
                      answer?.questionText?.translationID
                  )?.[0]?.QUESTION_OPTIONS;

            return {
              type,
              placeholder: {
                TRANSLATION_ID: answer?.questionText.translationID,
                DEFAULT_MESSAGE: answer?.questionText.defaultMessage,
              },
              options,
              checked: answer?.value,
            };
          });

        setFormState(prevFormState => ({
          ...prevFormState,
          contactInfo: contactInfo ?? prevFormState?.contactInfo,
          questionsAnswered,
          dropdownItems: dropdownItems ?? prevFormState?.dropdownItems,
          radioButtonItems: radioItems ?? prevFormState?.radioButtonItems,
        }));
        if (!dropdownItems) {
          // might be old data or empty. need to set state to original after api response
          setDriverFormData({ ...driverFormData });
        }
      }
    }
  }, [contactId, contactList]);

  // Set the initial formState from operations
  useEffect(() => {
    const stepperProgress = stepperKeys.reduce(
      (prev, curr) => ({
        ...prev,
        [curr]:
          Object.keys(prev).length === 0
            ? StepperProgressTypes.PRESENT
            : StepperProgressTypes.FUTURE,
      }),
      {}
    );

    const pages: PageItem[] = [];

    stepperKeys.forEach(key => {
      const points = formInformation?.stepperBulletPoints?.[key]?.points;
      if (!points) {
        return;
      }
      for (let i = 0; i < points.length; i += MAX_STEPS_PER_PAGE) {
        const page: { text: I18NContent }[] = [];
        let innerIndex = i;
        while (points[innerIndex] && innerIndex < i + MAX_STEPS_PER_PAGE) {
          page.push({ text: points[innerIndex] });
          innerIndex++;
        }
        pages.push({ points: page, type: key });
      }
    });

    setFormState(prevFormState => ({
      ...prevFormState,
      stepperProgress,
      pages,
    }));
  }, [formInformation]);

  useEffect(() => {
    handleSelectDropdownValue('vertical')(selectedVertical);
  }, [selectedVertical]);

  useEffect(() => {
    const items = formState?.dropdownItems;
    if (items && selectedVertical) {
      const index = items.findIndex(t => t.type === 'vertical');
      const newItem = { ...items[index], selected: selectedVertical };
      items[index] = newItem;

      setFormState(prevFormState => ({
        ...prevFormState,
        dropdownItems: items,
      }));
    }
  }, [formState.dropdownItems, selectedVertical]);

  const handleSpecialDropdownItems = (
    dropdownItems: DropdownItem[],
    data: any
  ): DropdownItem[] => {
    dropdownItems = dropdownItems?.map((d: DropdownItem) => {
      switch (d.type) {
        case 'contact_review_form_type':
          d.selected = formType;
          break;
      }
      return d;
    });
    return dropdownItems;
  };

  const handleSelectDropdownValue = (type: string) => (selected: string) => {
    const items = formState?.dropdownItems;
    if (items) {
      const index = items.findIndex(t => t.type === type);
      const newItem = { ...items[index], selected };
      items[index] = newItem;
      if (items[index].type?.includes('vertical')) {
        items[index].selected = selectedVertical;
      }
      setFormState(prevFormState => ({
        ...prevFormState,
        dropdownItems: items,
      }));
    }
  };

  const handleSelectRadioValue = (type: string) => (checked: number) => {
    const items = formState?.radioButtonItems;
    if (items) {
      const index = items.findIndex(t => t.type === type);
      const newItem = { ...items[index], checked };
      items[index] = newItem;
      setFormState(prevFormState => ({
        ...prevFormState,
        radioButtonItems: items,
      }));
    }
  };

  useEffect(() => {
    if (contactList?.length > 0) {
      if (contactId) {
        const contactInfo = contactList?.find(c => c.contactID === contactId);
        setFormState(prevFormState => ({ ...prevFormState, contactInfo }));
      }
      setContactIDs(
        contactList
          .filter(
            contact =>
              contact.status ===
              RecommendedContactStatusOptionsObject.NOTREVIEWED
          )
          .map(c => c.contactID)
      );
    }
  }, [contactList]);

  // Get profile data for user on page load
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      if (!passedProfile) {
        const queryMigrationVariables = apiNaws.prepareQuery(
          getCurrentWeek(),
          userId,
          cscEntityPrefix
        );
        apiNaws.getProfileData(
          queryMigrationVariables,
          setProfile,
          setProfileState
        );
      }
    }
  }, [inspireOptions.state, userId]);

  useEffect(() => {
    if (!driverFormData?.[0]) return;
    setDriverFormData(driverFormData?.[0]);
    let defaultDropdownItems =
      driverFormData[0].SECTIONS.find(
        (section: Section) =>
          section.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_DROPDOWNS'
      )?.QUESTIONS.map((question: Question) => ({
        type: question.QUESTION_ID.toLowerCase(),
        placeholder: question.QUESTION_TEXT,
        options: question.QUESTION_OPTIONS || [],
        selected: undefined,
      })) || [];

    handleSpecialDropdownItems(defaultDropdownItems, driverFormData[0]);

    let defaultRadioButtons =
      driverFormData[0].SECTIONS.find(
        (section: Section) =>
          section.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_RADIO_BUTTONS'
      )?.QUESTIONS.map((question: Question) => ({
        type: question.QUESTION_ID.toLowerCase(),
        placeholder: question.QUESTION_TEXT,
        options: question.QUESTION_OPTIONS || [],
        checked: undefined,
      })) || [];

    const stepperBulletPoints: { [key: string]: StepperBulletPointConfig } =
      driverFormData[0].SECTIONS.filter(
        (section: Section) => section.TYPE === 'SELECTABLE_CARD'
      ).reduce((acc: any, section: any) => {
        if (section?.QUESTION_TEXT?.DEFAULT_MESSAGE) {
          const points = section.QUESTIONS.map((question: Question) => {
            return {
              defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
              translationID: question.QUESTION_TEXT.TRANSLATION_ID,
            } as I18NContent;
          });
          acc[section.QUESTION_TEXT.DEFAULT_MESSAGE] = {
            points: points,
            trackLength:
              section.QUESTIONS.length <= 2
                ? 6
                : Math.ceil(section.QUESTIONS.length / 2) * 5,
            customClass: '',
          };
        }
        return acc;
      }, {});

    // map the stepper bullet points to criteria on right side
    const reviewBarCriteria: { [key: string]: ReviewBarItem } =
      driverFormData[0].SECTIONS.filter(
        ({ TYPE }: { TYPE: string }) => TYPE === 'SELECTABLE_CARD'
      ).reduce((acc: any, section: Section) => {
        section.QUESTIONS.forEach(question => {
          if (
            section?.QUESTION_TEXT?.DEFAULT_MESSAGE &&
            question.QUESTION_OPTIONS
          ) {
            const expert = question.QUESTION_OPTIONS.filter(
              (option: QuestionOption) =>
                option.TRANSLATION_ID.includes('expert')
            ).map(option => {
              return {
                defaultMessage: option.DEFAULT_MESSAGE,
                translationID: option.TRANSLATION_ID,
              } as I18NContent;
            });

            const intermediate = question.QUESTION_OPTIONS.filter(
              (option: QuestionOption) =>
                option.TRANSLATION_ID.includes('intermediate')
            ).map(option => {
              return {
                defaultMessage: option.DEFAULT_MESSAGE,
                translationID: option.TRANSLATION_ID,
              } as I18NContent;
            });

            const novice = question.QUESTION_OPTIONS.filter(
              (option: QuestionOption) =>
                option.TRANSLATION_ID.includes('novice')
            ).map(option => {
              return {
                defaultMessage: option.DEFAULT_MESSAGE,
                translationID: option.TRANSLATION_ID,
              } as I18NContent;
            });

            acc[question.QUESTION_TEXT.DEFAULT_MESSAGE] = {
              expert: expert,
              intermediate: intermediate,
              novice: novice,
            };
          }
        });
        return acc;
      }, {});

    const additionalQuestionContext: { [key: string]: I18NContent } =
      driverFormData[0].SECTIONS.filter(
        ({ TYPE }: { TYPE: string }) => TYPE === 'SELECTABLE_CARD'
      ).reduce((acc: any, section: Section) => {
        section.QUESTIONS.forEach(question => {
          if (
            question?.QUESTION_TEXT?.DEFAULT_MESSAGE &&
            section.ADDITIONAL_QUESTION_CONTEXT
          ) {
            acc[question.QUESTION_TEXT.DEFAULT_MESSAGE] = {
              defaultMessage:
                section.ADDITIONAL_QUESTION_CONTEXT.DEFAULT_MESSAGE,
              translationID: section.ADDITIONAL_QUESTION_CONTEXT.TRANSLATION_ID,
            };
          }
        });

        return acc;
      }, {});

    setFormInformation({
      defaultDropdownItems,
      defaultRadioButtons,
      stepperBulletPoints,
      reviewBarCriteria,
      additionalQuestionContext,
    });

    setStepperKeys(Object.keys(stepperBulletPoints));

    setFormState(prevFormState => ({
      ...prevFormState,
      dropdownItems: defaultDropdownItems,
    }));
  }, [[driverFormData]]);

  useEffect(() => {
    scrollToStepperColumns(showStepperColumns, 'stepper-columns');
  }, [showStepperColumns]);

  const openFeedbackModal = () => {
    setShowContactReviewFeedbackModal({
      open: true,
      status: FormSaveType.DELIVERED,
    });
  };

  let totalPoints = 0;
  for (const p of formState?.pages ?? []) {
    totalPoints += p?.points?.length;
  }

  const pageTypes = formState?.pages?.map(page => page.type.toLowerCase());

  const acceptableDropdowns =
    formState?.dropdownItems?.filter(
      d => d?.selected !== undefined && !pageTypes?.includes(d?.type)
    )?.length ?? 0;

  const totalRequiredPoints = acceptableDropdowns + totalPoints;

  const unselectedDropdown =
    formState?.dropdownItems?.filter(item => item.selected === undefined)
      .length ?? 0;

  let unSelectedRadio =
    formState?.radioButtonItems?.filter(
      radioItem =>
        radioItem.type !== 'was_account_security_was_not_followed' &&
        (radioItem.checked === undefined || radioItem.checked === null)
    ).length ?? 0;
  const questionsAnsweredKeys = Object.keys(
    formState?.questionsAnswered ?? []
  )?.filter((c: any) => !c.includes('*'));

  const unansweredQuestions =
    questionsAnsweredKeys?.filter((key: string) => {
      const value: AnsweredQuestionItem | undefined =
        formState?.questionsAnswered?.[key];
      // Special Case - Value not get updated with formState - Race Condition
      if (key === "If no, which aspect of Account Security wasn't followed?") {
        const requiredRadioItem = formState?.radioButtonItems?.find(
          item => item.type === 'was_account_security_verified'
        );
        const requiredRadioItemValue = requiredRadioItem?.checked;
        if (requiredRadioItemValue === 1) {
          return false;
        }

        if (requiredRadioItemValue === 0) {
          const optionalRadioValue = formState?.radioButtonItems?.find(
            item => item.type === 'was_account_security_was_not_followed'
          )?.checked;

          if (optionalRadioValue !== undefined || optionalRadioValue !== null) {
            return false;
          }
        }
      }

      return value?.checkedValue === undefined || value?.checkedValue === null;
    })?.length === 0;

  const canSave =
    questionsAnsweredKeys?.length + acceptableDropdowns >=
      totalRequiredPoints &&
    unansweredQuestions &&
    unselectedDropdown === 0 &&
    unSelectedRadio === 0;

  const isReviewedOrDelivered = isFormReviewedOrDelivered(
    contactId,
    formState?.contactInfo?.contactStatus
  );

  const handleSaveAsDraft = () => handleSaveLogic(FormSaveType.DRAFT);
  const handleSave = async (feedbackDetails: ContactDetailsInput) => {
    if (!canSave) {
      return;
    }
    handleSaveLogic(FormSaveType.DELIVERED, feedbackDetails);
  };

  const handleSaveLogic = async (
    status: FormSaveType,
    feedbackDetails?: ContactDetailsInput
  ) => {
    // Extract the necessary data from the form state
    const {
      dropdownItems = [],
      questionsAnswered,
      radioButtonItems = [],
    } = formState;
    const {
      FORM_ID: formID,
      VERSION: version,
      SECTIONS,
      TITLE: title,
    } = driverFormData;
    const formAnswers: DynamicFormAnswerInput[] = [];

    // Map dropdown items to the appropriate format
    dropdownItems.forEach((item: DropdownItem) => {
      const section: Section | undefined = SECTIONS.find(
        (section: Section) =>
          section.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_DROPDOWNS'
      );

      const question: Question | undefined = section?.QUESTIONS.find(
        (question: Question) =>
          question.QUESTION_ID === `${item.type?.toUpperCase()}`
      );

      if (question) {
        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        const specialFormTypeCase = {
          defaultMessage: title?.DEFAULT_MESSAGE,
          translationID: 'cs_perfportal_offroad_driver_review_form_title',
        };

        const selectedOption = item.selected;
        let selectedOptionText = question.QUESTION_OPTIONS?.find(
          option => option.DEFAULT_MESSAGE === selectedOption
        );

        const selectedOptionInput: I18NContentInput | undefined =
          selectedOptionText
            ? {
                translationID: selectedOptionText.TRANSLATION_ID,
                defaultMessage: selectedOptionText.DEFAULT_MESSAGE,
              }
            : question.QUESTION_ID === 'CONTACT_REVIEW_FORM_TYPE'
              ? specialFormTypeCase
              : undefined;

        const valueOfOption =
          selectedOptionText &&
          selectedOptionText.VALUE !== undefined &&
          selectedOptionText.VALUE !== null
            ? { value: selectedOptionText.VALUE }
            : {};

        formAnswers.push({
          questionID: question.QUESTION_ID,
          questionText,
          selectedOption: selectedOptionInput,
          ...valueOfOption,
        });
      }
    });

    // Map radio items to the appropriate format
    radioButtonItems.forEach((item: RadioButtonType) => {
      const section: Section | undefined = SECTIONS.find(
        (section: Section) =>
          section.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_RADIO_BUTTONS'
      );

      const question: Question | undefined = section?.QUESTIONS.find(
        (question: Question) =>
          question.QUESTION_ID === `${item.type?.toUpperCase()}`
      );

      if (question) {
        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        const selectedOption = item.checked;

        let selectedOptionText = question.QUESTION_OPTIONS?.find(
          option => option.VALUE === selectedOption
        );

        const selectedOptionInput: I18NContentInput | undefined =
          selectedOptionText
            ? {
                translationID: selectedOptionText.TRANSLATION_ID,
                defaultMessage: selectedOptionText.DEFAULT_MESSAGE,
              }
            : undefined;

        const valueOfOption =
          selectedOptionText &&
          selectedOptionText.VALUE !== undefined &&
          selectedOptionText.VALUE !== null
            ? { value: selectedOptionText.VALUE }
            : {};

        formAnswers.push({
          questionID: question.QUESTION_ID,
          questionText,
          selectedOption: selectedOptionInput,
          ...valueOfOption,
        });
      }
    });

    const freetextSections: Section[] = SECTIONS.filter(
      (section: Section) =>
        section.SECTION_ID === 'OFFROAD_DRIVER_REVIEW_FORM_FREE_TEXT'
    );

    const associateSiteSection = freetextSections.find(
      section => section.QUESTIONS[0].QUESTION_ID === 'ASSOCIATE_SITE'
    );

    if (associateSiteSection) {
      const questionText: I18NContentInput = {
        translationID:
          associateSiteSection.QUESTIONS[0].QUESTION_TEXT.TRANSLATION_ID,
        defaultMessage:
          associateSiteSection.QUESTIONS[0].QUESTION_TEXT.DEFAULT_MESSAGE,
      };

      formAnswers.push({
        questionID: associateSiteSection.QUESTIONS[0].QUESTION_ID,
        questionText,
        answerText: associateSite,
      });
    }

    const correctSOPSection = freetextSections.find(
      section => section.QUESTIONS[0].QUESTION_ID === 'CORRECT_SOP_NAME'
    );

    if (correctSOPSection) {
      const questionText: I18NContentInput = {
        translationID:
          correctSOPSection.QUESTIONS[0].QUESTION_TEXT.TRANSLATION_ID,
        defaultMessage:
          correctSOPSection.QUESTIONS[0].QUESTION_TEXT.DEFAULT_MESSAGE,
      };

      formAnswers.push({
        questionID: correctSOPSection.QUESTIONS[0].QUESTION_ID,
        questionText,
        answerText: correctSOP,
      });
    }

    const missingSOPSection = freetextSections.find(
      section => section.QUESTIONS[0].QUESTION_ID === 'SOP_MISSING'
    );

    if (missingSOPSection) {
      const questionText: I18NContentInput = {
        translationID:
          missingSOPSection.QUESTIONS[0].QUESTION_TEXT.TRANSLATION_ID,
        defaultMessage:
          missingSOPSection.QUESTIONS[0].QUESTION_TEXT.DEFAULT_MESSAGE,
      };

      formAnswers.push({
        questionID: missingSOPSection.QUESTIONS[0].QUESTION_ID,
        questionText,
        answerText: missingSOP,
      });
    }

    const sopUsedDuringAuditSection = freetextSections.find(
      section => section.QUESTIONS[0].QUESTION_ID === 'SOP_USED_DURING_AUDIT'
    );

    if (sopUsedDuringAuditSection) {
      const questionText: I18NContentInput = {
        translationID:
          sopUsedDuringAuditSection.QUESTIONS[0].QUESTION_TEXT.TRANSLATION_ID,
        defaultMessage:
          sopUsedDuringAuditSection.QUESTIONS[0].QUESTION_TEXT.DEFAULT_MESSAGE,
      };

      formAnswers.push({
        questionID: sopUsedDuringAuditSection.QUESTIONS[0].QUESTION_ID,
        questionText,
        answerText: sopUsedDuringAudit,
      });
    }

    const duplicatedDownItemsPlaceHolders = formState?.dropdownItems
      ?.map(p => p.placeholder.DEFAULT_MESSAGE)
      ?.filter(m => m.includes('*'));

    const duplicatedRadioPlaceHolders = formState?.radioButtonItems?.map(
      p => p.placeholder.DEFAULT_MESSAGE
    );

    // Map the questions answered to the appropriate format
    Object.keys(questionsAnswered ?? {}).forEach((key: string) => {
      if (duplicatedDownItemsPlaceHolders?.includes(key)) {
        // skip all dropdown items asl they are already accounted for
        return;
      }

      if (duplicatedRadioPlaceHolders?.includes(key)) {
        // skip all radio items asl they are already accounted for
        return;
      }

      const questionData: AnsweredQuestionItem | undefined =
        questionsAnswered?.[key];

      if (
        questionData?.checkedValue === undefined &&
        questionData?.comments === undefined
      ) {
        return;
      }

      const section: Section | undefined = SECTIONS.find((section: Section) =>
        section.QUESTIONS.some(
          (question: Question) => question.QUESTION_TEXT.DEFAULT_MESSAGE === key
        )
      );
      const question: Question | undefined = section?.QUESTIONS.find(
        (question: Question) => question.QUESTION_TEXT.DEFAULT_MESSAGE === key
      );

      if (question) {
        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        const answerText = questionData?.comments;

        let defaultMessage: string;
        let translationID: string;
        if (questionData.checkedValue !== undefined) {
          defaultMessage = getCheckedValueSelectionAsString(
            questionData.checkedValue
          );
          translationID =
            'cs_perfportal_sds_review_form_' +
            getCheckedValueSelectionAsString(
              questionData.checkedValue
            ).toLocaleLowerCase();
        }

        const selectedOption =
          questionData.checkedValue !== undefined
            ? {
                defaultMessage: defaultMessage!,
                translationID: translationID!,
              }
            : undefined;

        formAnswers.push({
          questionID: question.QUESTION_ID,
          questionText,
          answerText,
          selectedOption: selectedOption,
          value: questionData?.checkedValue,
        });
      }
    });

    const contactSummarySection = freetextSections.find(
      section => section.QUESTIONS[0].QUESTION_ID === 'CONTACT_SUMMARY'
    );

    if (contactSummarySection) {
      const questionText: I18NContentInput = {
        translationID:
          contactSummarySection.QUESTIONS[0].QUESTION_TEXT.TRANSLATION_ID,
        defaultMessage:
          contactSummarySection.QUESTIONS[0].QUESTION_TEXT.DEFAULT_MESSAGE,
      };

      formAnswers.push({
        questionID: contactSummarySection.QUESTIONS[0].QUESTION_ID,
        questionText,
        answerText: contactSummary,
      });
    }

    const formDetailsInput: FormDetailsInput = {
      formID: formID,
      version: version,
      formAnswers,
      contactStatus: status,
    };

    const selectedContact = contactList.filter(
      c => c.contactID === contactId
    )[0];

    const vertical = formState?.dropdownItems?.find(
      d => d.type === 'vertical'
    )?.selected;

    const contactDetailsInput = mergeFeedbackIntoContactDetails(
      feedbackDetails,
      selectedContact,
      vertical,
      formDetailsInput,
      ingress ?? ''
    );

    const updateFormReviewInput = {
      userName: profile.user.userName,
      userID: selectedContact?.userID,
      year: selectedContact?.calYear,
      week: selectedContact?.calWeek,
      contactID: contactId,
      reviewStatus: ContactStatusOptionsObject.REVIEWED,
      contactDetailsInput,
      isReviewedByCSA: false,
      contactStatus: status,
      formDetails: formDetailsInput,
      inAppFlag: status === ContactStatusOptionsObject.DELIVERED,
      emailFlag: emailChecked,
      locale: getLocaleString(),
      creationTimestamp: creationTimestamp,
    };

    try {
      // Send the data to the backend
      await apiNaws.updateFormReview(
        updateFormReviewInput,
        setFormStatusCode,
        setFormStatusCodeState,
        setExceptionMessage
      );
      if (
        ingress === OUTLIER_INGRESS &&
        isFeatureEnabled(Feature.OUTLIER_MANAGEMENT) &&
        updateFormReviewInput.contactDetailsInput.goodRecommendation ===
          GoodRecommendationOptionsObject.NO &&
        status === FormSaveType.DELIVERED
      ) {
        const writeOutlierExceptionInput =
          apiNaws.prepareQueryForWriteOutlierException(
            requesterUserID, //userId
            profile.user.userName, //deliveredToUsername
            selectedContact?.userID, //deliveredToUserId
            contactId, //contactId
            selectedContact?.calYear, //outlierCalYear
            selectedContact?.calWeek //outlierCalWeek
          );
        await apiNaws.writeOutlierException(
          writeOutlierExceptionInput,
          setOutlierResp,
          setOutlierRespState
        );
      }
      if (status === FormSaveType.DRAFT) {
        setIsSavingDraft(true);
        let redirectUrl = `${urls.contactReview}/${userId}/${contactId}`;
        const urlParams = new URLSearchParams(window.location.search);

        if (ingress === 'realtimeHMD') {
          urlParams.set('ingress', 'realtimeHMD');
        }
        if (ingress === OUTLIER_INGRESS) {
          urlParams.set('ingress', OUTLIER_INGRESS);
        }
        redirectUrl += `?${urlParams}`;
        navigate(renderUrl(redirectUrl), { replace: true });
      }
    } catch (error) {
      console.error('Error saving data', error);
    }
  };

  const handleClickBackButton = () => {
    const { currentPage } = formState;
    if (!currentPage) {
      return;
    }
    setFormState(prevFormState => ({
      ...prevFormState,
      currentPage: currentPage - 1,
    }));
    setCheckedValue(stepperKeys[findIndexOfStepper(checkedValue) - 1]);
  };

  const findIndexOfStepper = (checkedValue: string) => {
    return stepperKeys.findIndex(value => value === checkedValue);
  };

  const handleClickNextButton = () => {
    setShowStepperColumns(true);
  };

  const handleStepperRightColumnClickNextPage = () => {
    const { currentPage, pages, stepperProgress } = formState;
    if (currentPage !== undefined && pages && stepperProgress) {
      const newStepperProgress = { ...stepperProgress };
      const currentPageType = pages[currentPage].type;
      if (currentPage === pages.length - 1) {
        // On the last page, only need to set the last stepper to past
        newStepperProgress[currentPageType] = StepperProgressTypes.PAST;
        setFormState(prevFormState => ({
          ...prevFormState,
          stepperProgress: newStepperProgress,
        }));
        return;
      }

      const nextPage = currentPage + 1;
      const newPageType = pages[nextPage].type;
      // If the page changed the stepper step, we need to update that as well
      if (newPageType !== currentPageType) {
        newStepperProgress[currentPageType] = StepperProgressTypes.PAST;
        newStepperProgress[newPageType] = StepperProgressTypes.PRESENT;
      }
      setFormState(prevFormState => ({
        ...prevFormState,
        currentPage: nextPage,
        stepperProgress: newStepperProgress,
      }));
      setNextButtonRefresh(nextButtonRefresh + 1);
      setCheckedValue(stepperKeys[findIndexOfStepper(checkedValue) + 1]);
    }
  };

  const handleQuestionAnswered =
    (item: string) => (data: AnsweredQuestionItem) => {
      const { questionsAnswered } = formState;
      const copy = { ...questionsAnswered };
      copy[item] = data;
      setFormState(prevFormState => ({
        ...prevFormState,
        questionsAnswered: copy,
      }));
    };

  const handleFeedbackSubmitted = async (
    feedbackDetails: ContactDetailsInput
  ): Promise<any> => {
    const status = showContactReviewFeedbackModal?.status;
    setShowContactReviewFeedbackModal({
      open: false,
      status: undefined,
    });
    switch (status) {
      case FormSaveType.DELIVERED:
        await handleSave(feedbackDetails);
        break;
      case FormSaveType.DRAFT:
      default:
        await handleSaveAsDraft();
        break;
    }
    let redirectUrl = `${urls.coachingNotes}/${userId}?href=${ContactViewTabObject.REVIEWED}`;

    if (ingress === 'realtimeHMD') {
      redirectUrl += `&ingress=realtimeHMD`;
    } else if (ingress === OUTLIER_INGRESS) {
      redirectUrl = urls.outlierManagement;
    }

    setTimeout(() => {
      navigate(renderUrl(redirectUrl), { replace: true });
    }, 3000);
  };

  const onSelectCardClick = (key: string, index: any) => {
    setCheckedValue(key);
    formState.currentPage = index;
  };

  const stepperLeftColumn = (
    <>
      {formState?.pages?.map(
        (pageItem: PageItem, index: number, array: PageItem[]) => {
          const key = pageItem.type;
          const point = pageItem.points[0];
          const checkedValueSelection = formState?.questionsAnswered?.[
            point.text.defaultMessage
          ]
            ? getCheckedValueSelectionAsString(
                formState.questionsAnswered[point.text.defaultMessage]
                  .checkedValue
              )
            : '';
          return (
            <Card
              spacingInset='none'
              checked={checkedValue === key}
              onClick={() => onSelectCardClick(key, index)}
              width={300}
              value={key}
            >
              <Row
                spacingInset='400'
                alignmentHorizontal='justify'
                alignmentVertical='top'
                height={40}
              >
                <Text tag='label' type='h200' htmlFor={key.toLocaleLowerCase()}>
                  {key}
                </Text>
                <Column alignmentHorizontal='end'>
                  <CardRadioButton id={key.toLocaleLowerCase()} />
                </Column>
              </Row>
              <Row
                spacingInset='400'
                alignmentHorizontal='justify'
                alignmentVertical='top'
                height={44}
              >
                <Text
                  type={'h100'}
                  className={
                    checkedValueSelection === 'Expert'
                      ? styles.expert
                      : checkedValueSelection === 'Novice'
                        ? styles.novice
                        : styles.intermediate
                  }
                >
                  {checkedValueSelection}
                </Text>
              </Row>
            </Card>
          );
        }
      )}
    </>
  );

  const stepperRightColumn = (
    <Box spacingInset='100' width='auto' height='467'>
      {formState?.pages?.[formState?.currentPage ?? 0]?.points.map(
        (point: { text: I18NContent }) => (
          <>
            {formInformation?.additionalQuestionContext?.[
              point.text.defaultMessage
            ] && (
              <Box spacingInset='none none 450'>
                <Text>
                  <FormattedMessage
                    id={
                      formInformation?.additionalQuestionContext?.[
                        point.text.defaultMessage
                      ]?.translationID
                    }
                    defaultMessage={
                      formInformation?.additionalQuestionContext?.[
                        point.text.defaultMessage
                      ]?.defaultMessage
                    }
                  />
                </Text>
              </Box>
            )}
            <Text type={'d50'} className={styles.stepperLabel}>
              {formState?.pages?.[formState?.currentPage ?? 0]?.type}
            </Text>
            <ReviewBar
              key={point.text.defaultMessage}
              heading={point.text}
              bulletPoints={
                formInformation?.reviewBarCriteria?.[point.text.defaultMessage]
              }
              onChange={handleQuestionAnswered(point.text.defaultMessage)}
              data={formState?.questionsAnswered?.[point.text.defaultMessage]}
              forceResetState={formState?.currentPage}
              placeholder={intl.formatMessage({
                id: 'cs_perfportal_contact_review_table_note_placeholder',
                defaultMessage: 'Add notes here (Optional)',
                description: 'Note',
              })}
              disabled={isReviewedOrDelivered}
            />
          </>
        )
      )}
      <div className={styles.rightButtonsContainer}>
        <div className={styles.backButtonContainer}>
          <Button
            type='secondary'
            disabled={formState?.currentPage === 0}
            onClick={handleClickBackButton}
            {...csaFields(
              'button',
              'back',
              `cs_perfportal_offroad_review_back-${formState?.currentPage}`,
              ingress,
              !!ccxDetailsInfo
            )}
          >
            <FormattedMessage
              id='cs_perfportal_offroad_review_back'
              defaultMessage={getDefaultMessageByID(
                driverFormData,
                'cs_perfportal_tx_monitoring_back'
              )}
            />
          </Button>
        </div>
        <div className={styles.nextButtonContainer}>
          {(formState?.currentPage ?? 0) !==
            (formState?.pages?.length ?? 1) - 1 && (
            <Button
              disabled={formState?.nextButtonDisabled}
              onClick={handleStepperRightColumnClickNextPage}
              {...csaFields(
                'button',
                'next',
                `cs_perfportal_offroad_review_next-${formState?.currentPage}`,
                ingress,
                !!ccxDetailsInfo
              )}
            >
              <FormattedMessage
                id='cs_perfportal_tx_monitoring_next'
                defaultMessage={getDefaultMessageByID(
                  driverFormData,
                  'cs_perfportal_tx_monitoring_next'
                )}
              />
            </Button>
          )}
        </div>
      </div>
    </Box>
  );

  if (driverFormDataState === DataState.LOADING) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' data-testid='loader' />
      </div>
    );
  }

  // Form can not be (re)submitted when its state is LOADING or LOADED.
  const isFormLoadingOrLoaded =
    formStatusCodeState === DataState.LOADING ||
    formStatusCodeState === DataState.LOADED;

  const isSaveDraftButtonDisabled =
    isReviewedOrDelivered || isFormLoadingOrLoaded;
  const isDeliverButtonDisabled =
    !canSave || isReviewedOrDelivered || isFormLoadingOrLoaded;
  // defining separate param for email checkbox to decouple it from the logic for the deliver button
  // yet giving the flexibility to differentiate them when needed.
  const isEmailCheckBoxDisabled = isDeliverButtonDisabled;

  return (
    <>
      {driverFormData && driverFormDataState === DataState.LOADED && (
        <>
          <Box className={styles.offRoadDriverFormContainer}>
            <Column
              alignmentVertical='top'
              className={styles.radiodropdownParent}
            >
              <Row className={styles.title}>
                <Text type='h400'>
                  <FormattedMessage
                    id='cs_perfportal_non_delivery_review_form_title'
                    defaultMessage='Non-Delivery review form'
                  />
                </Text>
              </Row>
              <Row
                widths={['grid-3', 'grid-9']}
                alignmentVertical='top'
                alignmentHorizontal='start'
              >
                <Box>
                  <RenderDropdownsFromData
                    data={driverFormData}
                    formPrefix=''
                    dropdownSectionID='OFFROAD_DRIVER_REVIEW_FORM_DROPDOWNS'
                    styles={styles}
                    handleSelectDropdownValue={handleSelectDropdownValue}
                    formState={formState}
                    setFormState={setFormState}
                  />
                  <Column className={styles.contactSummary}>
                    <Text type='b300' className={styles.contactSummary}>
                      <FormattedMessage
                        id='cs_perfportal_sds_review_form_associate_site'
                        defaultMessage='Associate Site'
                      />
                    </Text>
                    <Textarea
                      value={associateSite}
                      onChange={handleAssociateSiteChange}
                      disabled={isReviewedOrDelivered}
                      error={associateSite.length > 500}
                      errorMessage={
                        associateSite.length > 500
                          ? intl.formatMessage({
                              id: 'cs_perfportal_coaching_comment_comment_size_error',
                              defaultMessage:
                                'Comment must be under 500 characters.',
                            })
                          : ''
                      }
                      width='310px'
                      helperText=''
                    />
                  </Column>
                </Box>
              </Row>
              {enableSDSOffroadDriverFormUpdates && (
                <Row>
                  <Box>
                    <Text type='b300' className={styles.contactSummary}>
                      <FormattedMessage
                        id='cs_perfportal_sds_review_form_sop_used_during_audit'
                        defaultMessage='What is the SOP used during audit?'
                      />
                    </Text>
                    <Textarea
                      value={sopUsedDuringAudit}
                      onChange={handleSopUsedDuringAudit}
                      error={sopUsedDuringAudit.length > 500}
                      disabled={isReviewedOrDelivered}
                      errorMessage={
                        sopUsedDuringAudit.length > 500
                          ? intl.formatMessage({
                              id: 'cs_perfportal_coaching_comment_comment_size_error',
                              defaultMessage:
                                'Comment must be under 500 characters.',
                            })
                          : ''
                      }
                      width='210px'
                      helperText=''
                    />
                  </Box>
                </Row>
              )}
              <Row>
                <RadioButtonsFromData
                  data={driverFormData}
                  formPrefix=''
                  radioSectionID='OFFROAD_DRIVER_REVIEW_FORM_RADIO_BUTTONS'
                  styles={styles}
                  handleSelectRadioValue={handleSelectRadioValue}
                  disabled={isReviewedOrDelivered}
                  formState={formState}
                  setFormState={setFormState}
                  selectedValue={undefined}
                  heading={'h100'}
                />
              </Row>
              {!enableSDSOffroadDriverFormUpdates && (
                <Row>
                  <Box>
                    <Text type='b300'>
                      <FormattedMessage
                        id='cs_perfportal_sds_review_form_correct_sop_name'
                        defaultMessage='Correct SOP Name'
                      />
                    </Text>
                    <Textarea
                      value={correctSOP}
                      onChange={handleCorrectSOP}
                      error={correctSOP.length > 500}
                      disabled={isReviewedOrDelivered}
                      errorMessage={
                        correctSOP.length > 500
                          ? intl.formatMessage({
                              id: 'cs_perfportal_coaching_comment_comment_size_error',
                              defaultMessage:
                                'Comment must be under 500 characters.',
                            })
                          : ''
                      }
                      width='210px'
                      helperText=''
                    />
                  </Box>
                </Row>
              )}
              {!enableSDSOffroadDriverFormUpdates && (
                <Row>
                  <Box>
                    <Text type='b300' className={styles.contactSummary}>
                      <FormattedMessage
                        id='cs_perfportal_sds_review_form_what_was_missing_in_sop_name'
                        defaultMessage='What was missing/incorrect in the SOP?'
                      />
                    </Text>
                    <Textarea
                      value={missingSOP}
                      onChange={handleMissingSOP}
                      error={missingSOP.length > 500}
                      disabled={isReviewedOrDelivered}
                      errorMessage={
                        missingSOP.length > 500
                          ? intl.formatMessage({
                              id: 'cs_perfportal_coaching_comment_comment_size_error',
                              defaultMessage:
                                'Comment must be under 500 characters.',
                            })
                          : ''
                      }
                      width='210px'
                      helperText=''
                    />
                  </Box>
                </Row>
              )}
            </Column>
            <div className={styles.nextButtonContainer}>
              {(formState?.currentPage ?? 0) !==
                (formState?.pages?.length ?? 1) - 1 &&
                !showStepperColumns && (
                  <Button
                    disabled={
                      !(unselectedDropdown === 0 && unSelectedRadio === 0)
                    }
                    onClick={handleClickNextButton}
                    {...csaFields(
                      'button',
                      'next',
                      `cs_perfportal_recipient_review_next-${formState?.currentPage}`,
                      ingress,
                      !!ccxDetailsInfo
                    )}
                  >
                    <FormattedMessage
                      id='cs_perfportal_tx_monitoring_next'
                      defaultMessage={getDefaultMessageByID(
                        driverFormData,
                        'cs_perfportal_tx_monitoring_next'
                      )}
                    />
                  </Button>
                )}
            </div>
            {formType && showStepperColumns && (
              <span id='stepper-columns'>
                <Row
                  className={styles.formStepperContainer}
                  alignmentVertical='top'
                >
                  <Column className={styles.leftCol}>
                    {stepperLeftColumn}
                  </Column>
                  <Column className={styles.rightCol}>
                    {stepperRightColumn}
                  </Column>
                </Row>
                <Column
                  spacingInset='300 300'
                  spacing='400'
                  width={'100%'}
                  type='outline'
                  className={styles.contactSummary}
                >
                  <Text type='b400'>
                    <div style={{ color: '#3690A1' }}>
                      <FormattedMessage
                        id='cs_perfportal_sds_review_form_contact_summary'
                        defaultMessage='Contact Summary'
                      />
                    </div>
                  </Text>
                  <Textarea
                    value={contactSummary}
                    disabled={isReviewedOrDelivered}
                    onChange={handleContactSummaryChange}
                    placeholder={intl.formatMessage({
                      id: 'cs_perfportal_contact_review_label_feedback_note',
                      defaultMessage:
                        'Additional notes or comments? (Optional)',
                    })}
                    width='100%'
                    error={false}
                    helperText=''
                  />
                </Column>
                <Column className={styles.buttonsContainer}>
                  <Row alignmentHorizontal='center'>
                    <Button
                      onClick={handleSaveAsDraft}
                      type='secondary'
                      className={styles.saveButton}
                      disabled={isSaveDraftButtonDisabled}
                      {...csaFields(
                        'button',
                        'save as draft',
                        'cs_perfportal_tx_monitoring_draft',
                        ingress,
                        !!ccxDetailsInfo
                      )}
                    >
                      <FormattedMessage
                        id='cs_perfportal_tx_monitoring_draft'
                        defaultMessage={
                          grabExtraFieldByID(
                            driverFormData[0],
                            'cs_perfportal_tx_monitoring_draft'
                          )?.DEFAULT_MESSAGE
                        }
                      />
                    </Button>
                    <Button
                      data-testid='deliver-button'
                      onClick={openFeedbackModal}
                      className={styles.saveButton}
                      disabled={isDeliverButtonDisabled}
                      {...csaFields(
                        'button',
                        'deliver',
                        'cs_perfportal_tx_monitoring_deliver',
                        ingress,
                        !!ccxDetailsInfo
                      )}
                    >
                      <FormattedMessage
                        id='cs_perfportal_tx_monitoring_deliver'
                        defaultMessage={
                          grabExtraFieldByID(
                            driverFormData[0],
                            'cs_perfportal_tx_monitoring_deliver'
                          )?.DEFAULT_MESSAGE
                        }
                      />
                    </Button>
                  </Row>
                  <Row
                    className={styles.emailCheckboxContainer}
                    alignmentHorizontal='center'
                  >
                    <Checkbox
                      checked={emailChecked}
                      onChange={setEmailChecked}
                      disabled={isEmailCheckBoxDisabled}
                      data-csa-c-type='element'
                      data-csa-c-slot-id='tx-monitoring-email-notification-slot'
                      data-csa-c-content-id='tx-monitoring-email-notification-checkbox'
                      data-csa-c-checked={`${emailChecked}`}
                    >
                      <FormattedMessage
                        id='cs_perfportal_tx-monitoring_email_notification'
                        defaultMessage='E-mail summary to associate'
                      />
                    </Checkbox>
                  </Row>
                </Column>
              </span>
            )}
          </Box>
        </>
      )}
      {!driverFormData && <Loader data-testid='loader' />}
      {contactId && (
        <ContactReviewFeedbackModal
          open={showContactReviewFeedbackModal?.open}
          parentValues={
            contactList?.filter(c => c.contactID === contactId)?.[0] ?? {}
          }
          parentUserName={profile?.user?.userName}
          parentUserID={userId ?? ''}
          onFeedbackSubmitted={handleFeedbackSubmitted}
          contactTab={ContactViewTabObject.RECOMMENDED}
          contactCategory='Corrective'
          ingress={ingress}
        />
      )}
      <TimeoutToast
        show={isAdmin && showErrorToast}
        message={exceptionMessage}
        duration={3000}
        type='error'
        onClose={(): void => {
          setShowErrorToast(false);
        }}
      />
      <DraftSavedToast
        show={showDraftSavedToast}
        onClose={() => setShowDraftSavedToast(false)}
      />
    </>
  );
};

export default OffRoadDriverReviewForm;
